import { z } from 'zod';
import { REMINDER_STATUSES, REMINDER_TYPES, SCHEDULE_TYPES } from '@irl/types';
import { validReminderSubstitutions } from '../reminders/substitutions.js';
import { isValidLocalDateString } from '../schedule/recurrence-rule.js';
import { objectIdSchema, scheduleConfigSchema, timezoneSchema } from './common.js';
import { confirmationButtonsSchema } from './confirmation-buttons.js';
import { reminderPollSchema } from './reminder-poll.js';
import { reminderSubstitutionsSchema } from './reminder-substitutions.js';
import { reminderRichMessageSchema } from './rich-message.js';
const localDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .refine(isValidLocalDateString, 'Date must be a valid calendar date');
export const confirmationConfigSchema = z
    .object({
    buttons: confirmationButtonsSchema.optional(),
    repeatEnabled: z.boolean().default(false),
    audience: z.enum(['all', 'selected']),
    userIds: z.array(objectIdSchema).max(500).default([]),
    intervalMinutes: z.number().int().min(5).max(10080),
    deadlineHours: z.number().int().min(1).max(720),
})
    .refine((value) => value.audience !== 'selected' || value.userIds.length > 0, {
    message: 'Select at least one participant',
    path: ['userIds'],
})
    .refine((value) => !value.repeatEnabled || value.intervalMinutes < value.deadlineHours * 60, {
    message: 'Interval must be shorter than the response period',
    path: ['intervalMinutes'],
})
    .transform(({ buttons, ...config }) => ({ ...config, ...(buttons ? { buttons } : {}) }));
const reminderBaseSchema = z.object({
    substitutions: reminderSubstitutionsSchema.nullable().optional(),
    poll: reminderPollSchema.nullable().optional(),
    confirmation: confirmationConfigSchema.optional(),
    chatId: objectIdSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    type: z.enum(REMINDER_TYPES),
    status: z.enum(REMINDER_STATUSES).default('draft'),
    startDate: z.coerce.date(),
    startDateLocal: localDateSchema.optional(),
    endDate: z.coerce.date().optional(),
    timezone: timezoneSchema,
    scheduleType: z.enum(SCHEDULE_TYPES),
    scheduleConfig: scheduleConfigSchema,
    targetChatIds: z.array(objectIdSchema).default([]),
    targetUserIds: z.array(objectIdSchema).default([]),
    messageTemplate: z.string().min(1).max(4096),
    richMessage: reminderRichMessageSchema.optional(),
    useRichFormat: z.boolean().default(false),
});
export const createReminderSchema = reminderBaseSchema
    .refine(validReminderSubstitutions, {
    message: 'Configure substitutions and use a valid date offset',
    path: ['substitutions'],
})
    .refine((data) => data.type !== 'task' || Boolean(data.confirmation), {
    message: 'Confirmation settings required',
    path: ['confirmation'],
})
    .refine((data) => !data.endDate || data.endDate >= data.startDate, {
    message: 'endDate must be after startDate',
    path: ['endDate'],
})
    .refine((data) => !data.useRichFormat || (data.richMessage !== undefined && data.richMessage.blocks.length > 0), {
    message: 'richMessage must contain at least one block when useRichFormat is enabled',
    path: ['richMessage'],
});
export const updateReminderSchema = reminderBaseSchema
    .partial()
    .extend({
    status: z.enum(REMINDER_STATUSES).optional(),
    targetChatIds: z.array(objectIdSchema).optional(),
    targetUserIds: z.array(objectIdSchema).optional(),
    useRichFormat: z.boolean().optional(),
})
    .superRefine((data, ctx) => {
    if (data.useRichFormat === true) {
        if (!data.richMessage || data.richMessage.blocks.length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'richMessage must contain at least one block when useRichFormat is enabled',
                path: ['richMessage'],
            });
        }
    }
});
export const reminderIdParamSchema = z.object({
    id: objectIdSchema,
});
export const chatRemindersParamSchema = z.object({
    id: objectIdSchema,
});
export const reminderListQuerySchema = z.object({
    search: z.string().trim().max(200).optional(),
    upcoming: z.enum(['true']).optional(),
    chatId: objectIdSchema.optional(),
    status: z.enum(REMINDER_STATUSES).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.enum(['createdAt', 'nextRunAt', 'title']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const schedulePreviewSchema = z.object({
    scheduleType: z.enum(SCHEDULE_TYPES),
    scheduleConfig: scheduleConfigSchema,
    timezone: timezoneSchema,
    startDate: z.coerce.date(),
    startDateLocal: localDateSchema.optional(),
    endDate: z.coerce.date().optional(),
    count: z.coerce.number().int().min(1).max(20).default(10),
});
export const reminderNamePreviewQuerySchema = z.object({
    chatId: objectIdSchema,
    timezone: timezoneSchema,
    scheduledAt: z.coerce.date(),
});
//# sourceMappingURL=reminder.js.map